import PropTypes from "prop-types";
// @mui
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItem from "@mui/material/ListItem";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
// utils
import { es, enUS } from "date-fns/locale";
import { formatDistanceToNow } from "date-fns";
// components
import Iconify from "src/components/iconify";
import CustomPopover, { usePopover } from "src/components/custom-popover";
import { useLocales } from "src/locales";
import { useAuthContext } from "src/auth/hooks";

import { paths } from "src/routes/paths";
import { USER_NOTIFICATION } from "src/core/lib/constants";
// api
import { remove, unread } from "src/api/notification";

// ----------------------------------------------------------------------

export default function NotificationItem({ notification, mutate }) {
  const { t } = useLocales();
  const { user } = useAuthContext();
  const language = () => {
    switch (user.language) {
      case "es":
        return es;
      case "en":
        return enUS;
      default:
        return enUS;
    }
  };

  const renderAvatar = (
    <ListItemAvatar>
      {notification.icon ? (
        <Avatar
          src={notification.icon}
          sx={{ bgcolor: "background.neutral" }}
        />
      ) : (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: "background.neutral",
          }}
        >
          <Iconify
            icon="mdi:notifications"
            width={24}
          />
        </Stack>
      )}
    </ListItemAvatar>
  );

  const renderText = (
    <ListItemText
      disableTypography
      primary={reader(notification.title)}
      secondary={
        <>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ typography: "body2", color: "text.strong", mb: 0.5 }}
          >
            {notification.body}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ typography: "caption", color: "text.disabled" }}
          >
            {formatDistanceToNow(new Date(notification.triggeredDate), {
              addSuffix: true,
              locale: language(),
            })}
          </Stack>
        </>
      }
    />
  );

  const renderUnReadBadge = !notification.read && (
    <Box
      sx={{
        top: 20,
        width: 8,
        height: 8,
        right: 30,
        borderRadius: "50%",
        bgcolor: "info.main",
        position: "absolute",
      }}
    />
  );

  const popover = usePopover();

  const handleDelete = async () => {
    popover.onClose();
    await remove(notification.id);
    mutate();
  };

  const handleUnread = async () => {
    popover.onClose();
    await unread(notification.id);
    mutate();
  };

  const entityTypeString = `${notification.entityType
    ?.charAt(0)
    .toLowerCase()}${notification.entityType?.slice(1)}`;

  return (
    <>
      <ListItem
        secondaryAction={
          <IconButton
            color={popover.open ? "inherit" : "default"}
            onClick={popover.onOpen}
          >
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        }
        disablePadding
      >
        <ListItemButton
          disableRipple
          sx={{
            p: 2.5,
            alignItems: "flex-start",
            borderBottom: theme => `dashed 1px ${theme.palette.divider}`,
          }}
          component="a"
          href={
            notification.notificationType === USER_NOTIFICATION.workRequestResolved
              ? paths.dashboard.workRequest.conformity(notification.entityId)
              : notification.entityType &&
                paths.dashboard[entityTypeString].edit(notification.entityId)
          }
        >
          {renderUnReadBadge}

          {renderAvatar}

          <Stack sx={{ flexGrow: 1 }}>{renderText}</Stack>
        </ListItemButton>
      </ListItem>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 220 }}
      >
        <MenuItem onClick={handleUnread}>
          <Iconify icon="eva:cloud-download-fill" />
          {t("markAsUnread")}
        </MenuItem>

        <MenuItem
          onClick={handleDelete}
          sx={{ color: "error.main" }}
        >
          <Iconify icon="solar:trash-bin-trash-bold" />
          {t("delete")}
        </MenuItem>
      </CustomPopover>
    </>
  );
}

NotificationItem.propTypes = {
  notification: PropTypes.object,
  mutate: PropTypes.any,
};

// ----------------------------------------------------------------------

function reader(data) {
  return (
    <Box
      dangerouslySetInnerHTML={{ __html: data }}
      sx={{
        mb: 0.5,
        "& p": { typography: "body2", m: 0 },
        "& a": { color: "inherit", textDecoration: "none" },
        "& strong": { typography: "subtitle2" },
      }}
    />
  );
}
